import * as React from "react"
import { Calendar, ExternalLink, ImageIcon } from "lucide-react"
import { CarouselWidget, CarouselWidgetContent, CarouselWidgetItem, CarouselWidgetNext, CarouselWidgetPrevious } from "./carousel-widget"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./card"
import { ButtonWidget } from "./button-widget"
import { cn } from "../../lib/utils"

export interface ContentEntry {
  title: string
  description: string
  date: string
  link: string
  linkText?: string
  image?: string
}

interface CarouselViewProps {
  entries: ContentEntry[]
  className?: string
  showImages?: boolean
  itemsPerView?: number
}

const CarouselView = React.forwardRef<
  React.ElementRef<typeof CarouselWidget>,
  CarouselViewProps
>(({ entries, className, showImages = true, itemsPerView = 3, ...props }, ref) => {
  const opts = {
    align: "start" as const,
    loop: false,
  }

  return (
    <CarouselWidget
      ref={ref}
      opts={opts}
      className={cn("dsw-w-full dsw-max-w-full", className)}
      {...props}
    >
      <CarouselWidgetContent className="dsw--ml-4">
        {entries.map((entry, index) => (
          <CarouselWidgetItem key={index} className={cn("dsw-pl-4", `dsw-basis-1/${itemsPerView}`)}>
            <Card className="dsw-h-full dsw-flex dsw-flex-col">
              {showImages && (
                <div className="dsw-aspect-video dsw-bg-muted dsw-flex dsw-items-center dsw-justify-center dsw-rounded-t-lg">
                  {entry.image ? (
                    <img 
                      src={entry.image} 
                      alt={entry.title}
                      className="dsw-w-full dsw-h-full dsw-object-cover dsw-rounded-t-lg"
                    />
                  ) : (
                    <ImageIcon className="dsw-h-12 dsw-w-12 dsw-text-muted-foreground" />
                  )}
                </div>
              )}
              <CardHeader className="dsw-flex-1">
                <CardTitle className="dsw-line-clamp-2 dsw-text-sm">{entry.title}</CardTitle>
                <CardDescription className="dsw-line-clamp-3 dsw-text-xs">
                  {entry.description}
                </CardDescription>
              </CardHeader>
              <CardContent className="dsw-pt-0">
                <div className="dsw-flex dsw-items-center dsw-justify-between dsw-gap-2">
                  <span className="dsw-flex dsw-items-center dsw-gap-1 dsw-text-xs dsw-text-muted-foreground">
                    <Calendar className="dsw-h-3 dsw-w-3" />
                    {entry.date}
                  </span>
                  <ButtonWidget variant="outline" size="sm" asChild>
                    <a href={entry.link} target="_blank" rel="noopener noreferrer" className="dsw-flex dsw-items-center">
                      <ExternalLink className="dsw-h-3 dsw-w-3 dsw-mr-1" />
                      {entry.linkText || "View"}
                    </a>
                  </ButtonWidget>
                </div>
              </CardContent>
            </Card>
          </CarouselWidgetItem>
        ))}
      </CarouselWidgetContent>
      <CarouselWidgetPrevious />
      <CarouselWidgetNext />
    </CarouselWidget>
  )
})
CarouselView.displayName = "CarouselView"

export { CarouselView } 